// Platform Icons Carousel Component
import React, { useRef, useEffect, useState } from "react";

const PlatformIconsCarousel = ({ platforms, renderPlatformIcon }) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);

  const hasManyPlatforms = platforms?.length > 3;

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (!hasManyPlatforms) return;

    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(dragOffset);
    containerRef.current.style.cursor = "grabbing";
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !hasManyPlatforms) return;

    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Multiply for faster scrolling
    const newOffset = scrollLeft + walk;

    // Calculate boundaries
    const containerWidth = containerRef.current.parentElement.offsetWidth;
    const contentWidth = containerRef.current.scrollWidth;
    const maxOffset = Math.min(0, containerWidth - contentWidth - 20); // 20px padding

    // Constrain the offset
    const constrainedOffset = Math.max(maxOffset, Math.min(0, newOffset));
    setDragOffset(constrainedOffset);
    containerRef.current.style.transform = `translateX(${constrainedOffset}px)`;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = hasManyPlatforms ? "grab" : "default";
    }
  };

  // Touch handlers for mobile
  const handleTouchStart = (e) => {
    if (!hasManyPlatforms) return;

    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(dragOffset);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || !hasManyPlatforms) return;

    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    const newOffset = scrollLeft + walk;

    const containerWidth = containerRef.current.parentElement.offsetWidth;
    const contentWidth = containerRef.current.scrollWidth;
    const maxOffset = Math.min(0, containerWidth - contentWidth - 20);

    const constrainedOffset = Math.max(maxOffset, Math.min(0, newOffset));
    setDragOffset(constrainedOffset);
    containerRef.current.style.transform = `translateX(${constrainedOffset}px)`;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Add global mouse up listener
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        handleMouseMove(e);
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mousemove", handleGlobalMouseMove);

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mousemove", handleGlobalMouseMove);
    };
  }, [isDragging, startX, scrollLeft]);

  // Reset position when platforms change
  useEffect(() => {
    setDragOffset(0);
    if (containerRef.current) {
      containerRef.current.style.transform = "translateX(0px)";
    }
  }, [platforms]);

  return (
    <div
      className={`platform-icons ${
        hasManyPlatforms ? "many-platforms" : "few-platforms"
      }`}
    >
      {hasManyPlatforms && <div className="drag-hint">Drag to see more</div>}

      <div
        ref={containerRef}
        className="platform-icons-container"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${dragOffset}px)`,
          cursor: hasManyPlatforms ? "grab" : "default",
        }}
      >
        {platforms?.map((platform, index) => (
          <React.Fragment key={`${platform}-${index}`}>
            {renderPlatformIcon(platform)}
          </React.Fragment>
        ))}
      </div>

      {hasManyPlatforms && (
        <div className="platform-count-badge">{platforms.length}</div>
      )}
    </div>
  );
};

// Usage in your existing component:
// Replace your existing platform-icons div with:
/*
<PlatformIconsCarousel 
  platforms={topic.platform} 
  renderPlatformIcon={renderPlatformIcon} 
/>
*/

export default PlatformIconsCarousel;
